import { Component, Input, OnDestroy } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject, forkJoin, of } from 'rxjs';
import { catchError, finalize, map, takeUntil } from 'rxjs/operators';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { ToastrService } from 'ngx-toastr';
import { CommonService } from '../services/common.service';
import { VarulesComponent } from '../varules/varules.component';

type FaxStatusFilter = 'all' | 'active' | 'inactive';
type FaxLookupFilterKey = 'organization' | 'facility' | 'orderingPhysician' | 'pathologist';

interface FaxFilterOption {
  displayname: string;
  value: string;
  Type?: string;
  entityid?: string;
  organizationid?: string;
  LocationGUID?: string;
  UniqueID?: string;
}

interface FaxFilterField {
  key: FaxLookupFilterKey;
  label: string;
  placeholder: string;
  data: FaxFilterOption[];
  isLoading: boolean;
  form: FormGroup;
}

interface FaxRuleFilters {
  organization: FaxFilterOption | null;
  facility: FaxFilterOption | null;
  orderingPhysician: FaxFilterOption | null;
  pathologist: FaxFilterOption | null;
}

interface FaxExclusionRow {
  ExclusionId: number | null;
  RuleId: number | null;
  EntityType: string;
  EntityId: string;
}

interface FaxPendingRuleSelect {
  ruleId?: number | null;
  ruleName?: string;
  showCreateToast?: boolean;
}

@Component({
  selector: 'app-fax-varules',
  templateUrl: './fax-varules.component.html',
  styleUrls: ['./fax-varules.component.scss']
})
export class FaxVarulesComponent implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private modalInput: any;
  private lastLoadedKey = '';

  searchvalue = '';
  statusFilter: FaxStatusFilter = 'all';
  showFilterPanel = false;
  draftFilters: FaxRuleFilters = this.emptyFilters();
  appliedFilters: FaxRuleFilters = this.emptyFilters();
  readonly keywordsearch = 'displayname';
  readonly entitySearchFilter = (items: FaxFilterOption[]): FaxFilterOption[] => items;
  readonly selectedValueRender = (item: any): string => {
    if (item == null || item === '') {
      return '';
    }
    if (typeof item === 'string' || typeof item === 'number') {
      return this.stripHtml(String(item));
    }
    return this.stripHtml(item.displayname || String(item.value || ''));
  };
  notFoundData = false;
  readonly filterFields: FaxFilterField[] = this.buildFilterFields();
  private readonly filterSearchGen: Record<string, number> = {};
  rulelist: any[] = [];
  selectedRule: any = null;
  highlightedRuleId: number | null = null;
  private pendingRuleSelect: FaxPendingRuleSelect | null = null;
  pageIndex = 0;
  readonly pageSize = 15;

  showExclusions = false;
  exclusions: FaxExclusionRow[] = [];
  exclusionId = 0;
  exclusionModel = { EntityType: '', EntityId: '' };
  selectedExclusionRow = -1;
  entityTypes: { displayname: string; value: string }[] = [];
  entityIdOptions: FaxFilterOption[] = [];
  entityIdLoading = false;
  entityIdNotFound = false;
  readonly entityIdForm = new FormGroup({ value: new FormControl('') });
  private editBaseline = { EntityType: '', EntityId: '' };
  private entityTypesLoaded = false;
  private exclusionsByRuleId = new Map<number, FaxExclusionRow[]>();
  private exclusionEntityDisplay = new Map<string, string>();
  private entityIdSearchGen = 0;

  constructor(
    public activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private service: CommonService,
    private toastr: ToastrService,
    private ngxUiLoaderService: NgxUiLoaderService
  ) {}

  get islocked(): boolean {
    return !!this.modalInput?.islocked;
  }

  get canSubmitExclusion(): boolean {
    if (this.islocked) {
      return false;
    }
    if (this.exclusionId > 0) {
      return this.isExclusionFormDirty();
    }
    return true;
  }

  @Input()
  set inputDataInModalComponent(value: any) {
    this.modalInput = value;
    this.loadEntityTypes();
    this.loadFaxRules();
  }

  get inputDataInModalComponent(): any {
    return this.modalInput;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get filteredRules(): any[] {
    const query = (this.searchvalue || '').trim().toLowerCase();
    const matched = this.rulelist.filter((rule) => this.matchesFilters(rule, query));
    if (!query) {
      return matched;
    }
    return matched.sort((a, b) => this.compareSearchRank(a, b, query));
  }

  get pagedRules(): any[] {
    const start = this.pageIndex * this.pageSize;
    return this.filteredRules.slice(start, start + this.pageSize);
  }

  get totalCount(): number {
    return this.rulelist.length;
  }

  get activeCount(): number {
    return this.rulelist.filter((rule) => this.isActive(rule)).length;
  }

  get inactiveCount(): number {
    return this.rulelist.filter((rule) => !this.isActive(rule)).length;
  }

  get activeFilterCount(): number {
    return [
      this.appliedFilters.organization,
      this.appliedFilters.facility,
      this.appliedFilters.orderingPhysician,
      this.appliedFilters.pathologist
    ].filter((item) => !!item?.value).length;
  }

  setStatusFilter(filter: FaxStatusFilter): void {
    this.statusFilter = filter;
    this.resetPage();
  }

  onSearchChange(): void {
    this.resetPage();
  }

  clearSearch(): void {
    this.searchvalue = '';
    this.onSearchChange();
  }

  toggleFilterPanel(): void {
    this.showFilterPanel = !this.showFilterPanel;
    if (this.showFilterPanel) {
      this.restoreDraftFilters();
    }
  }

  applyFilters(): void {
    this.appliedFilters = {
      organization: this.cloneOption(this.draftFilters.organization),
      facility: this.cloneOption(this.draftFilters.facility),
      orderingPhysician: this.cloneOption(this.draftFilters.orderingPhysician),
      pathologist: this.cloneOption(this.draftFilters.pathologist)
    };
    this.showFilterPanel = false;
    this.resetPage();
  }

  clearFilters(): void {
    this.draftFilters = this.emptyFilters();
    this.appliedFilters = this.emptyFilters();
    this.resetFilterFields();
    this.resetPage();
  }

  onFilterInput(field: FaxFilterField, query: string): void {
    const value = (query || '').trim();
    const selected = this.draftFilters[field.key];
    if (selected && this.stripHtml(selected.displayname) === this.stripHtml(value)) {
      field.isLoading = false;
      return;
    }
    if (value.length < 3) {
      field.isLoading = false;
      return;
    }
    this.searchFilterOptions(field, value);
  }

  onFilterSelected(item: FaxFilterOption, field: FaxFilterField): void {
    const displayname = this.stripHtml(item?.displayname || '');
    this.draftFilters[field.key] = {
      displayname,
      value: item?.value || '',
      UniqueID: item?.UniqueID || '',
      entityid: item?.entityid || '',
      organizationid: item?.organizationid || ''
    };
  }

  onFilterCleared(field: FaxFilterField): void {
    this.draftFilters[field.key] = null;
    field.data = [];
    field.form.get('value')?.setValue('');
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.syncSelectedRule();
  }

  selectRule(rule: any): void {
    this.selectedRule = rule;
  }

  isActive(rule: any): boolean {
    return String(rule?.Status) === '1';
  }

  openExclusions(rule: any): void {
    if (!rule?.RuleId) {
      return;
    }
    this.selectedRule = rule;
    this.showExclusions = true;
    this.clearExclusionForm();
    this.loadEntityTypes();
    const cached = this.exclusionsByRuleId.get(rule.RuleId);
    if (cached) {
      this.exclusions = cached.map((row) => ({ ...row }));
      this.resolveExclusionEntityDisplays(this.exclusions);
      return;
    }
    this.loadExclusions(rule.RuleId);
  }

  closeExclusions(): void {
    this.showExclusions = false;
    this.clearExclusionForm();
  }

  clearExclusionForm(): void {
    this.exclusionId = 0;
    this.selectedExclusionRow = -1;
    this.exclusionModel = { EntityType: '', EntityId: '' };
    this.editBaseline = { EntityType: '', EntityId: '' };
    this.resetEntityIdSearch();
  }

  clearEntityType(): void {
    if (this.islocked) {
      return;
    }
    this.exclusionModel.EntityType = '';
    this.onEntityTypeChange();
  }

  onEntityTypeChange(): void {
    this.exclusionModel.EntityId = '';
    this.resetEntityIdSearch();
  }

  onEntityIdInput(query: string): void {
    const value = (query || '').trim();
    const selectedLabel = this.selectedValueRender(this.entityIdForm.get('value')?.value);
    if (this.exclusionModel.EntityId && this.stripHtml(value) === selectedLabel) {
      this.entityIdLoading = false;
      return;
    }
    if (value.length < 3 || this.islocked) {
      this.entityIdLoading = false;
      return;
    }
    const filterType = this.getExclusionFilterType(this.exclusionModel.EntityType);
    this.searchEntityIdOptions(filterType, value);
  }

  onEntityIdSelected(item: FaxFilterOption): void {
    this.exclusionModel.EntityId = String(item?.value || '').trim();
    if (!this.exclusionModel.EntityType && item?.Type) {
      this.exclusionModel.EntityType = item.Type;
    }
    this.rememberExclusionEntityDisplay(
      this.exclusionModel.EntityType || item?.Type || '',
      this.exclusionModel.EntityId,
      item?.displayname
    );
  }

  formatExclusionEntityId(row: FaxExclusionRow): string {
    const entityType = String(row?.EntityType || '').trim();
    const entityId = String(row?.EntityId || '').trim();
    if (!entityId) {
      return '—';
    }
    return this.exclusionEntityDisplay.get(this.exclusionDisplayKey(entityType, entityId)) || entityId;
  }

  onEntityIdCleared(): void {
    this.exclusionModel.EntityId = '';
    this.entityIdOptions = [];
    this.entityIdNotFound = false;
    this.entityIdForm.get('value')?.setValue('');
  }

  editExclusion(row: FaxExclusionRow, index: number): void {
    if (this.islocked) {
      return;
    }
    this.selectedExclusionRow = index;
    this.exclusionId = row.ExclusionId || 0;
    this.exclusionModel = {
      EntityType: row.EntityType || '',
      EntityId: row.EntityId || ''
    };
    this.editBaseline = {
      EntityType: row.EntityType || '',
      EntityId: row.EntityId || ''
    };
    this.entityIdOptions = [];
    this.entityIdNotFound = false;
    this.entityIdForm.get('value')?.setValue(row.EntityId || '');
  }

  async saveExclusion(): Promise<void> {
    if (!this.canSubmitExclusion) {
      return;
    }
    const fields = this.readExclusionFormFields();
    if (!fields) {
      return;
    }
    const { entityType, entityId } = fields;
    if (this.hasDuplicateExclusion(entityType, entityId)) {
      this.toastr.warning('Record Already Exists', '', {
        timeOut: 4000,
        positionClass: 'toast-bottom-right'
      });
      return;
    }
    const deploymentkey = this.modalInput?.deploymentkey;
    const ruleId = this.selectedRule?.RuleId;
    if (!deploymentkey || !ruleId) {
      return;
    }
    const isUpdate = this.exclusionId > 0;
    this.ngxUiLoaderService.start();
    const response = await this.service.dxpostapiconn(
      'api/VitalHubDXAPI/savevaruleexclusion',
      {
        ExclusionId: this.exclusionId || 0,
        RuleId: ruleId,
        EntityType: entityType,
        EntityId: entityId
      },
      deploymentkey
    );
    this.ngxUiLoaderService.stop();
    if (this.isDxTransportError(response)) {
      this.toastr.error('Something Went Wrong!', '', {
        timeOut: 4000,
        positionClass: 'toast-bottom-right'
      });
      return;
    }
    const savedId = Number(response?.ExclusionId ?? response?.exclusionId);
    if (!savedId || savedId === -1) {
      this.toastr.warning('Record Already Exists', '', {
        timeOut: 4000,
        positionClass: 'toast-bottom-right'
      });
      return;
    }
    this.applySavedExclusionRow({
      ExclusionId: savedId,
      RuleId: ruleId,
      EntityType: entityType,
      EntityId: entityId
    }, isUpdate);
    this.cacheExclusions(ruleId);
    this.toastr.success(
      isUpdate ? 'Exclusion Updated Successfully' : 'Exclusion Saved Successfully',
      '',
      {
        timeOut: 500,
        positionClass: 'toast-bottom-right'
      }
    );
    this.auditExclusionSave(ruleId, entityType, entityId, isUpdate);
    this.rememberExclusionEntityDisplay(entityType, entityId);
    this.clearExclusionForm();
  }

  private readExclusionFormFields(): { entityType: string; entityId: string } | null {
    const entityType = String(this.exclusionModel.EntityType || '').trim();
    const entityId = String(this.exclusionModel.EntityId || '').trim();
    if (!entityType || !entityId) {
      this.toastr.error('Please fill in the mandatory fields', '', {
        timeOut: 4000,
        positionClass: 'toast-bottom-right'
      });
      return null;
    }
    return { entityType, entityId };
  }

  private hasDuplicateExclusion(entityType: string, entityId: string): boolean {
    const typeKey = entityType.toLowerCase();
    const idKey = entityId.toLowerCase();
    return this.exclusions.some((item) =>
      item.ExclusionId !== this.exclusionId
      && String(item.EntityType || '').trim().toLowerCase() === typeKey
      && String(item.EntityId || '').trim().toLowerCase() === idKey
    );
  }

  private isDxTransportError(response: any): boolean {
    return !!(response?.status && response?.statusText);
  }

  private applySavedExclusionRow(savedRow: FaxExclusionRow, isUpdate: boolean): void {
    if (!isUpdate) {
      this.exclusions = [...this.exclusions, savedRow];
      return;
    }
    const index = this.exclusions.findIndex((item) => item.ExclusionId === this.exclusionId);
    if (index >= 0) {
      this.exclusions[index] = savedRow;
      return;
    }
    this.exclusions = [...this.exclusions, savedRow];
  }

  private auditExclusionSave(
    ruleId: number,
    entityType: string,
    entityId: string,
    isUpdate: boolean
  ): void {
    const newAuditValue = `RuleId ${ruleId}, EntityType=${entityType}, EntityId=${entityId}`;
    const oldAuditValue = isUpdate
      ? `RuleId ${ruleId}, EntityType=${this.editBaseline.EntityType || ''}, EntityId=${this.editBaseline.EntityId || ''}`
      : '';
    void this.service.auditsave(
      isUpdate ? 'Update' : 'Add',
      oldAuditValue,
      newAuditValue,
      'Pipeline',
      this.modalInput?.contextid,
      isUpdate ? 'VA Rule Exclusion Updated' : 'VA Rule Exclusion Added'
    );
  }

  async deleteExclusion(row: FaxExclusionRow): Promise<void> {
    if (this.islocked || !row?.ExclusionId) {
      return;
    }
    const deploymentkey = this.modalInput?.deploymentkey;
    const ruleId = this.selectedRule?.RuleId;
    if (!deploymentkey || !ruleId) {
      return;
    }
    this.ngxUiLoaderService.start();
    const response = await this.service.dxdeleteapi(
      'api/VitalHubDXAPI/deletevaruleexclusion?ExclusionId=' + row.ExclusionId,
      deploymentkey
    );
    this.ngxUiLoaderService.stop();
    if (this.isDxTransportError(response)) {
      this.toastr.error('Something Went Wrong!', '', {
        timeOut: 4000,
        positionClass: 'toast-bottom-right'
      });
      return;
    }
    this.exclusions = this.exclusions.filter((item) => item.ExclusionId !== row.ExclusionId);
    this.cacheExclusions(ruleId);
    this.toastr.success('Exclusion Deleted successfully', '', {
      timeOut: 500,
      positionClass: 'toast-bottom-right'
    });
    const oldAuditValue = `RuleId ${ruleId}, EntityType=${row.EntityType || ''}, EntityId=${row.EntityId || ''}`;
    void this.service.auditsave(
      'Delete',
      oldAuditValue,
      '',
      'Pipeline',
      this.modalInput?.contextid,
      'VA Rule Exclusion Deleted'
    );
    if (this.exclusionId === row.ExclusionId) {
      this.clearExclusionForm();
    }
  }

  openRule(rule: any): void {
    if (!rule?.RuleId) {
      return;
    }
    const editingRuleId = Number(rule.RuleId);
    const dataToruleModal = {
      ...(this.modalInput || {}),
      routename: rule.RuleName,
      preloadedRules: [JSON.parse(JSON.stringify(rule))],
      hideAddButton: true,
      fromFaxViewRule: true
    };
    const modalRef = this.modalService.open(VarulesComponent, {
      windowClass: 'myCustomModalClasscreatevarule',
      backdrop: 'static',
      keyboard: false
    });
    modalRef.componentInstance.inputDataInModalComponent = dataToruleModal;
    modalRef.result.then(
      () => this.onViewRuleClosed(editingRuleId),
      () => this.onViewRuleClosed(editingRuleId)
    );
  }

  private onViewRuleClosed(ruleId: number): void {
    if (!Number.isFinite(ruleId) || ruleId <= 0) {
      return;
    }
    this.pendingRuleSelect = { ruleId };
    this.reloadFaxRules();
  }

  openAddRule(): void {
    if (this.islocked) {
      return;
    }
    const mode = Number(this.modalInput?.Mode);
    const dataToruleModal = {
      ...(this.modalInput || {}),
      // Same Add VA Rule screen as normal Varules (no fax RuleName auto-build).
      hideAddButton: true,
      openCreateOnInit: true,
      fromFaxUi: true,
      Mode: Number.isFinite(mode) ? mode : this.modalInput?.Mode
    };
    const modalRef = this.modalService.open(VarulesComponent, {
      windowClass: 'myCustomModalClasscreatevarule',
      backdrop: 'static',
      keyboard: false
    });
    modalRef.componentInstance.inputDataInModalComponent = dataToruleModal;
    modalRef.result.then(
      (result: { action?: string; ruleId?: number | null; ruleName?: string }) => {
        if (result?.action === 'created') {
          this.pendingRuleSelect = { ruleId: result.ruleId, ruleName: result.ruleName, showCreateToast: true };
          this.statusFilter = 'all';
          this.showFilterPanel = false;
          this.appliedFilters = this.emptyFilters();
          this.draftFilters = this.emptyFilters();
          this.resetFilterFields();
          this.searchvalue = result.ruleName || (result.ruleId ? String(result.ruleId) : '');
          this.reloadFaxRules();
        }
      },
      () => { /* dismissed */ }
    );
  }

  private applyPendingRuleSelection(): void {
    const pending = this.pendingRuleSelect;
    if (!pending) {
      return;
    }
    this.pendingRuleSelect = null;

    let rule = pending.ruleId
      ? this.rulelist.find((item) => Number(item?.RuleId) === Number(pending.ruleId))
      : null;
    if (!rule && pending.ruleName) {
      const nameKey = pending.ruleName.toLowerCase();
      rule = this.rulelist.find((item) => String(item?.RuleName || '').toLowerCase() === nameKey);
    }

    if (rule) {
      const index = this.filteredRules.findIndex((item) => Number(item?.RuleId) === Number(rule.RuleId));
      if (index >= 0) {
        this.pageIndex = Math.floor(index / this.pageSize);
      }
      this.selectedRule = rule;
      this.highlightedRuleId = Number(rule.RuleId);
      setTimeout(() => {
        if (this.highlightedRuleId === Number(rule.RuleId)) {
          this.highlightedRuleId = null;
        }
      }, 8000);
    } else {
      this.syncSelectedRule();
    }

    if (pending.showCreateToast) {
      const label = rule?.RuleName || pending.ruleName || 'New rule';
      const idText = pending.ruleId ? ` (Rule Id ${pending.ruleId})` : (rule?.RuleId ? ` (Rule Id ${rule.RuleId})` : '');
      this.toastr.success(`Rule created successfully — ${label}${idText}`, '', {
        timeOut: 5000,
        positionClass: 'toast-bottom-right'
      });
    }
  }

  closepopup(): void {
    this.activeModal.dismiss();
  }

  private reloadFaxRules(): void {
    this.lastLoadedKey = '';
    this.loadFaxRules();
  }

  private loadEntityTypes(): void {
    if (this.entityTypesLoaded) {
      return;
    }
    this.entityTypesLoaded = true;
    this.service.getlookupInfo('exclusionentitytype').subscribe({
      next: (data) => {
        this.entityTypes = (Array.isArray(data) ? data : [])
          .map((item: any) => {
            const value = String(item?.lookupvalue ?? item?.LookupValue ?? item?.lookupid ?? item?.LookupId ?? '').trim();
            return value ? { displayname: value, value } : null;
          })
          .filter((item): item is { displayname: string; value: string } => !!item);
      },
      error: () => {
        this.entityTypesLoaded = false;
        this.entityTypes = [];
      }
    });
  }

  /**
   * Converts Entity Type to getvarulesbyfilter filtertype for Entity Id autocomplete.
   * Uses Excluded-* keys (ExternalOrderingFacilityID, ExCaseType, Exservices) instead of
   * OrderingFacilityID / CaseType / Services, because those include All/-1 options which
   * are not valid for exclusions; Excluded-* returns the same real ids without All.
   */
  private getExclusionFilterType(entityType: string): string {
    const key = String(entityType || '').trim().toLowerCase();
    if (key === 'orderingfacility') {
      return 'ExternalOrderingFacilityID';
    }
    if (key === 'orderingphysician') {
      return 'OrderingPhysician';
    }
    if (key === 'casetype') {
      return 'ExCaseType';
    }
    if (key === 'service') {
      return 'Exservices';
    }
    return '';
  }

  private resetEntityIdSearch(): void {
    this.entityIdSearchGen += 1;
    this.entityIdOptions = [];
    this.entityIdLoading = false;
    this.entityIdNotFound = false;
    this.entityIdForm.get('value')?.setValue('');
  }

  private searchEntityIdOptions(filterType: string, query: string): void {
    const deploymentkey = this.modalInput?.deploymentkey;
    if (!deploymentkey) {
      return;
    }
    const gen = ++this.entityIdSearchGen;
    this.entityIdLoading = true;
    const filters = filterType
      ? [{ filterType, entityType: this.getExclusionEntityType(filterType) }]
      : [
          { filterType: 'ExternalOrderingFacilityID', entityType: 'orderingfacility' },
          { filterType: 'OrderingPhysician', entityType: 'orderingphysician' },
          { filterType: 'ExCaseType', entityType: 'Casetype' },
          { filterType: 'Exservices', entityType: 'Service' }
        ];
    forkJoin(filters.map((filter) =>
      this.fetchFilterOptions(filter.filterType, query).pipe(
        map((items: any[]) => items.map((item) => ({ ...item, Type: filter.entityType })))
      )
    ))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (groups) => {
          if (gen !== this.entityIdSearchGen) {
            return;
          }
          this.entityIdOptions = this.mapAndRankFilterOptions(([] as any[]).concat(...groups), query);
          this.entityIdNotFound = this.entityIdOptions.length === 0;
          this.entityIdLoading = false;
        },
        error: () => {
          if (gen !== this.entityIdSearchGen) {
            return;
          }
          this.entityIdOptions = [];
          this.entityIdNotFound = true;
          this.entityIdLoading = false;
        }
      });
  }

  private getExclusionEntityType(filterType: string): string {
    const types: Record<string, string> = {
      ExternalOrderingFacilityID: 'orderingfacility',
      OrderingPhysician: 'orderingphysician',
      ExCaseType: 'Casetype',
      Exservices: 'Service'
    };
    return types[filterType] || '';
  }

  private isExclusionFormDirty(): boolean {
    return String(this.exclusionModel.EntityType || '').trim() !== String(this.editBaseline.EntityType || '').trim()
      || String(this.exclusionModel.EntityId || '').trim() !== String(this.editBaseline.EntityId || '').trim();
  }

  private cacheExclusions(ruleId: number): void {
    this.exclusionsByRuleId.set(ruleId, this.exclusions.map((row) => ({ ...row })));
  }

  private loadFaxRules(): void {
    const deploymentkey = this.modalInput?.deploymentkey;
    const mode = Number(this.modalInput?.Mode);
    if (!deploymentkey || !Number.isFinite(mode)) {
      return;
    }
    if (this.lastLoadedKey === deploymentkey) {
      return;
    }
    this.lastLoadedKey = deploymentkey;
    let url = 'api/VitalHubDXAPI/getvarule?Mode=' + mode;
    this.ngxUiLoaderService.start();
    this.service.dxgetapi(url, deploymentkey)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.ngxUiLoaderService.stop())
      )
      .subscribe({
        next: (data) => {
          this.rulelist = this.toRuleArray(data);
          this.resetPage();
          if (this.pendingRuleSelect) {
            this.applyPendingRuleSelection();
          }
        },
        error: () => {
          this.rulelist = [];
          this.resetPage();
          if (this.pendingRuleSelect) {
            this.pendingRuleSelect = null;
            this.toastr.error('Rule list could not be refreshed.', '', {
              timeOut: 4000,
              positionClass: 'toast-bottom-right'
            });
          }
        }
      });
  }

  private loadExclusions(ruleId: number): void {
    const deploymentkey = this.modalInput?.deploymentkey;
    if (!deploymentkey || !ruleId) {
      this.exclusions = [];
      return;
    }
    this.ngxUiLoaderService.start();
    this.service.dxgetapi('api/VitalHubDXAPI/getvaruleexclusions?RuleId=' + ruleId, deploymentkey)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.ngxUiLoaderService.stop())
      )
      .subscribe({
        next: (data) => {
          this.exclusions = this.toExclusionArray(data);
          this.resolveExclusionEntityDisplays(this.exclusions);
          this.cacheExclusions(ruleId);
        },
        error: () => {
          this.exclusions = [];
          this.exclusionsByRuleId.delete(ruleId);
          this.toastr.error('Failed to load exclusions', '', {
            timeOut: 4000,
            positionClass: 'toast-bottom-right'
          });
        }
      });
  }

  private toRuleArray(data: any): any[] {
    if (Array.isArray(data)) {
      return data;
    }
    if (Array.isArray(data?.responsebody)) {
      return data.responsebody;
    }
    return [];
  }

  private toExclusionArray(data: any): FaxExclusionRow[] {
    return this.toRuleArray(data).map((item) => ({
      ExclusionId: item.ExclusionId ?? item.exclusionId ?? null,
      RuleId: item.RuleId ?? item.ruleId ?? null,
      EntityType: item.EntityType ?? item.entityType ?? '',
      EntityId: item.EntityId ?? item.entityId ?? ''
    }));
  }

  private compareSearchRank(ruleA: any, ruleB: any, query: string): number {
    const rankDiff = this.getSearchRank(ruleB, query) - this.getSearchRank(ruleA, query);
    if (rankDiff !== 0) {
      return rankDiff;
    }
    if (/^\d+$/.test(query)) {
      const queryNum = Number(query);
      const diffA = Math.abs((Number(ruleA?.RuleId) || 0) - queryNum);
      const diffB = Math.abs((Number(ruleB?.RuleId) || 0) - queryNum);
      if (diffA !== diffB) {
        return diffA - diffB;
      }
    }
    return String(ruleA?.RuleName ?? '').localeCompare(String(ruleB?.RuleName ?? ''));
  }

  private getSearchRank(rule: any, query: string): number {
    const ruleId = String(rule?.RuleId ?? '').toLowerCase();
    const ruleName = String(rule?.RuleName ?? '').toLowerCase();
    if (ruleId === query) {
      return 4;
    }
    if (ruleId.startsWith(query)) {
      return 3;
    }
    if (ruleName === query || ruleName.startsWith(query)) {
      return 2;
    }
    if ([ruleId, ruleName].some((value) => value.includes(query))) {
      return 1;
    }
    return 0;
  }

  private matchesFilters(rule: any, query: string): boolean {
    if (this.statusFilter === 'active' && !this.isActive(rule)) {
      return false;
    }
    if (this.statusFilter === 'inactive' && this.isActive(rule)) {
      return false;
    }
    if (query && ![rule.RuleName, rule.RuleId, rule.LabName, rule.OrdFacilityName, rule.CaseTypeDisplayName]
      .some((value) => String(value || '').toLowerCase().includes(query))) {
      return false;
    }
    if (!this.matchesAppliedFilters(rule)) {
      return false;
    }
    return true;
  }

  private matchesAppliedFilters(rule: any): boolean {
    const filters = this.appliedFilters;
    if (!this.matchesSelected(filters.organization, [rule.LabOrgId, rule.OrdFacOrgId], [rule.LabOrgName, rule.OrdFacOrgName])) {
      return false;
    }
    if (!this.matchesSelected(filters.facility, [rule.LabId, rule.OrdFacilityId], [rule.LabName, rule.OrdFacilityName])) {
      return false;
    }
    if (!this.matchesSelected(filters.orderingPhysician, [rule.OrdPhysician], [rule.OrdPhysicianName])) {
      return false;
    }
    if (!this.matchesSelected(filters.pathologist, [rule.PathologistId], [rule.PathologistName])) {
      return false;
    }
    return true;
  }

  private matchesSelected(selected: FaxFilterOption | null, ids: any[], names: any[]): boolean {
    if (!selected) {
      return true;
    }
    if (!selected.value && !selected.LocationGUID && !selected.entityid && !selected.organizationid) {
      return true;
    }
    const candidates = [
      selected.value,
      selected.entityid,
      selected.organizationid,
      selected.LocationGUID,
      selected.UniqueID
    ].filter((value) => !!(value || '').trim()).map((value) => String(value));
    if (ids.some((value) => candidates.includes(String(value ?? '')))) {
      return true;
    }
    const nameOnly = (selected.displayname || '').toLowerCase().replace(/\s*\([^)]*\)\s*$/, '').trim();
    return !!nameOnly && names.some((value) => String(value || '').toLowerCase().includes(nameOnly));
  }

  private emptyFilters(): FaxRuleFilters {
    return {
      organization: null,
      facility: null,
      orderingPhysician: null,
      pathologist: null
    };
  }

  private cloneOption(option: FaxFilterOption | null): FaxFilterOption | null {
    if (!option?.value && !option?.LocationGUID) {
      return null;
    }
    return { ...option };
  }

  private restoreDraftFilters(): void {
    this.draftFilters = {
      organization: this.cloneOption(this.appliedFilters.organization),
      facility: this.cloneOption(this.appliedFilters.facility),
      orderingPhysician: this.cloneOption(this.appliedFilters.orderingPhysician),
      pathologist: this.cloneOption(this.appliedFilters.pathologist)
    };
    this.filterFields.forEach((field) => {
      const selected = this.draftFilters[field.key];
      field.form.get('value')?.setValue(selected?.displayname || '');
      field.data = selected ? [selected] : [];
      field.isLoading = false;
    });
  }

  private resetFilterFields(): void {
    this.filterFields.forEach((field) => {
      field.form.get('value')?.setValue('');
      field.data = [];
      field.isLoading = false;
    });
  }

  private searchFilterOptions(field: FaxFilterField, query: string): void {
    const deploymentkey = this.modalInput?.deploymentkey;
    if (!deploymentkey) {
      return;
    }
    const gen = (this.filterSearchGen[field.key] = (this.filterSearchGen[field.key] || 0) + 1);
    field.isLoading = true;
    forkJoin(this.getFilterTypes(field.key).map((filterType) => this.fetchFilterOptions(filterType, query)))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (groups) => {
          if (this.filterSearchGen[field.key] !== gen) {
            return;
          }
          field.data = this.mapAndRankFilterOptions(([] as any[]).concat(...groups), query);
          this.notFoundData = field.data.length === 0;
          field.isLoading = false;
        },
        error: () => {
          if (this.filterSearchGen[field.key] !== gen) {
            return;
          }
          field.data = [];
          this.notFoundData = true;
          field.isLoading = false;
        }
      });
  }

  private getFilterTypes(key: FaxLookupFilterKey): string[] {
    if (key === 'organization') {
      return ['LaboratoryOrganizationID', 'OrderingFacilityOrganizationID'];
    }
    if (key === 'facility') {
      return ['LaboratoryAccountID', 'OrderingFacilityID'];
    }
    if (key === 'orderingPhysician') {
      return ['OrderingPhysician'];
    }
    return ['PathologistID'];
  }

  private fetchFilterOptions(filterType: string, query: string) {
    const url =
      'api/VitalHubDXAPI/getvarulesbyfilter?filtertype=' +
      encodeURIComponent(filterType) +
      '&filtervalue=' +
      encodeURIComponent(query);
    return this.service.dxgetapi(url, this.modalInput?.deploymentkey).pipe(
      catchError(() => of([])),
      map((response: any) => (response?.status && response?.statusText) ? [] : (Array.isArray(response) ? response : []))
    );
  }

  private stripHtml(value: string): string {
    return String(value || '').replace(/<[^>]*>/g, '').trim();
  }

  private exclusionDisplayKey(entityType: string, entityId: string): string {
    return `${String(entityType || '').trim().toLowerCase()}:${String(entityId || '').trim()}`;
  }

  private rememberExclusionEntityDisplay(entityType: string, entityId: string, displayName?: string): void {
    const type = String(entityType || '').trim();
    const id = String(entityId || '').trim();
    if (!type || !id) {
      return;
    }
    let label = this.stripHtml(displayName || '').trim();
    if (!label) {
      label = this.stripHtml(this.selectedValueRender(this.entityIdForm.get('value')?.value)).trim();
    }
    if (!label || label === id) {
      return;
    }
    this.exclusionEntityDisplay.set(this.exclusionDisplayKey(type, id), label);
  }

  private resolveExclusionEntityDisplays(rows: FaxExclusionRow[]): void {
    const serviceRows = rows.filter((row) => {
      const entityType = String(row.EntityType || '').trim();
      const entityId = String(row.EntityId || '').trim();
      return entityType.toLowerCase() === 'service'
        && entityId
        && !this.exclusionEntityDisplay.has(this.exclusionDisplayKey(entityType, entityId));
    });
    if (!serviceRows.length) {
      return;
    }
    forkJoin(serviceRows.map((row) => {
      const entityId = String(row.EntityId).trim();
      return this.fetchFilterOptions('Exservices', entityId).pipe(
        map((items) => ({ row, items: Array.isArray(items) ? items : [] }))
      );
    }))
      .pipe(takeUntil(this.destroy$))
      .subscribe((groups) => {
        groups.forEach(({ row, items }) => {
          const entityId = String(row.EntityId).trim();
          const match = items.find((item) => String(item?.value ?? '').trim() === entityId) || items[0];
          if (!match) {
            return;
          }
          const label = this.formatServiceDisplayName(
            this.stripHtml(String(match.displayname ?? '')),
            entityId,
            'Service'
          );
          if (label) {
            this.exclusionEntityDisplay.set(this.exclusionDisplayKey(row.EntityType, entityId), label);
          }
        });
      });
  }

  private formatServiceDisplayName(displayname: string, value: string, type?: string): string {
    if (!displayname || !value || String(type || '').trim().toLowerCase() !== 'service') {
      return displayname;
    }
    const suffix = ` (${value})`;
    if (displayname.toLowerCase().endsWith(suffix.toLowerCase())) {
      return displayname;
    }
    return `${displayname}${suffix}`;
  }

  private mapAndRankFilterOptions(list: any[], query: string): FaxFilterOption[] {
    const q = (query || '').trim().toLowerCase();
    const seen = new Set<string>();
    return (Array.isArray(list) ? list : [])
      .map((item: any) => {
        const type = item?.Type != null ? String(item.Type) : '';
        const value = String(item?.value ?? '').trim();
        const rawName = this.stripHtml(String(item?.displayname ?? item?.label ?? ''));
        return {
          displayname: this.formatServiceDisplayName(rawName, value, type),
          value,
          Type: type,
          UniqueID: item?.UniqueID != null ? String(item.UniqueID) : '',
          entityid: item?.entityid != null ? String(item.entityid) : ''
        };
      })
      .filter((item) => {
        const name = (item.displayname || '').toLowerCase();
        const key = item.Type ? `${item.Type}:${item.value}` : item.value;
        if (!item.value || item.value === '-1' || name === 'all' || seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      })
      .sort((a, b) => {
        const ids = (option: FaxFilterOption) =>
          [option.UniqueID, option.entityid, option.value].map((id) => String(id || '').toLowerCase());
        return Number(ids(b).includes(q)) - Number(ids(a).includes(q));
      });
  }

  private buildFilterFields(): FaxFilterField[] {
    return [
      this.createFilterField('organization', 'Organization Id / Name', 'Search Lab Org or OF Org'),
      this.createFilterField('facility', 'Account Id/Name', 'Search Lab or OF Account'),
      this.createFilterField('orderingPhysician', 'Ordering Physician Id / Name', 'Search Ordering Physician'),
      this.createFilterField('pathologist', 'Pathologist Id / Name', 'Search Pathologist')
    ];
  }

  private createFilterField(key: FaxLookupFilterKey, label: string, placeholder: string): FaxFilterField {
    return {
      key,
      label,
      placeholder,
      data: [],
      isLoading: false,
      form: new FormGroup({ value: new FormControl('') })
    };
  }

  private resetPage(): void {
    this.pageIndex = 0;
    this.syncSelectedRule();
  }

  private syncSelectedRule(): void {
    const stillVisible = this.pagedRules.some((rule) => rule?.RuleId === this.selectedRule?.RuleId);
    if (!stillVisible) {
      this.selectedRule = this.pagedRules[0] || null;
    }
  }
}
